import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostBinding,
  HostListener,
  CUSTOM_ELEMENTS_SCHEMA,
  OnChanges,
  SimpleChanges,
  signal,
  computed,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  TemplateRef
} from '@angular/core';
import { FileUpload, GetSignedURLDto } from 'src/app/layout/shell/shell.model'
import { TablesService } from 'src/app/core/service/table.service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, Subscription, switchMap, tap } from 'rxjs';
import { ToastService } from '../../toast/toast.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-file-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './file-picker.component.html',
  styleUrls: ['./file-picker.component.scss']
})
export class FilePickerComponent implements OnChanges, OnInit, OnDestroy {

  private state = signal<{
    isMinimized: boolean;
    items: FileUpload[];
    initialItems: FileUpload[];
    isFullScreen: boolean;
    activeTableId: string;
    maximizeState: 'minimized' | 'thirty' | 'full';
  }>({
    isMinimized: false,
    items: [],
    initialItems: [],
    isFullScreen: false,
    activeTableId: '',
    maximizeState: 'thirty'
  });
  searchText$ = new BehaviorSubject('');
  private searchSubscription?: Subscription;

  @Input() files: FileUpload[] = [];
  @Input() selected_file_index: number = 0;
  @Input() filesCurrentlyUploading: number = 0;
  @Input() valid_MIME_types: string[] = [];
  @Input() valid_extensions: string[] = [];
  @Input() valid_files: string = '';
  @Input() tableId: string = '';
  @Input() isGeneratingSchema: boolean = false;

  @Output() fileSelected = new EventEmitter<number>();
  @Output() fileDropped = new EventEmitter<{ event: any, isDrop: boolean }>();
  @Output() fileInvalid = new EventEmitter<File[]>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('blackBarFileInput') blackBarFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('deleteFileModal') deleteFileModal!: TemplateRef<any>;

  @HostBinding('class.dragover') cDragOver = false;
  @HostBinding('class.dragidle') cDragIdle = true;
  @HostBinding('class.drop') cDrop = false;
  @HostBinding('class.drag-invalid') cDragInvalid = false;
  isDragOver = false;
  @HostBinding('class.overflows') overflows = false;
  @HostBinding('class.initializing') initializing = true;
  private dragLeaveTimeout: any;

  @HostBinding('class.minimized')
  get isMinimized(): boolean {
    return this.state().isMinimized;
  }

  @HostBinding('class.full-screen')
  get isFullScreen(): boolean {
    return this.state().isFullScreen;
  }

  @HostBinding('class.thirty-screen')
  get isThirtyScreen(): boolean {
    return this.state().maximizeState === 'thirty';
  }

  get maximizeState(): 'minimized' | 'thirty' | 'full' {
    return this.state().maximizeState;
  }

  cachedImageUrls: { [key: string]: string } = {};
  viewMode: 'grid' | 'list' = 'grid';
  sortActive = signal<boolean>(false);
  sortDirection = signal<'asc' | 'desc'>('asc');
  filterActive = signal<boolean>(false);
  
  uploadDateFilter: string = '';
  structuringDateFilter: string = '';
  alphanumericFilter: string = ''; 
  fileTypeFilter: string = '';
  fileSizeFilter: string = '';
  
  activeFiltersCount = signal<number>(0); 
  
  activeFileMenu: string | null = null;
  isRenaming: string | null = null;
  newFileName: string = '';
  fileToDelete: any = null;

  constructor(
    private tablesService: TablesService,
    private toastService: ToastService,
    private modalService: NgbModal
  ) {}

  readonly items = computed(() => this.state().items);
  readonly isMinimizedComputed = computed(() => this.state().isMinimized);
  readonly isFullScreenComputed = computed(() => this.state().isFullScreen);
  readonly activeTableId = computed(() => this.state().activeTableId);
  private readonly initialItems = computed(() => this.state().initialItems);


  ngOnInit(): void {
    this.setupSearch();
    this.initialize();
    this.preventGlobalDragDrop();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
    }
    this.removeGlobalDragDropPrevention();
  }

  private preventGlobalDragDrop(): void {
    document.addEventListener('dragover', this.preventDefaults, false);
    document.addEventListener('drop', this.preventDefaults, false);
    document.addEventListener('dragenter', this.preventDefaults, false);
    document.addEventListener('dragleave', this.preventDefaults, false);
  }

  private removeGlobalDragDropPrevention(): void {
    document.removeEventListener('dragover', this.preventDefaults, false);
    document.removeEventListener('drop', this.preventDefaults, false);
    document.removeEventListener('dragenter', this.preventDefaults, false);
    document.removeEventListener('dragleave', this.preventDefaults, false);
  }

  private preventDefaults = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  }




  get docsText(): string {
    const count = this.items().length;
    return `${count} document${count !== 1 ? 's' : ''} uploaded`;
  }

  get searchPhrase() {
    return this.searchText$;
  }

  readonly itemsToShow = computed(() => {
    let items = [...this.items()];
    
    const hasActiveFilters = this.uploadDateFilter || this.structuringDateFilter || 
                            this.alphanumericFilter || this.fileTypeFilter || this.fileSizeFilter;
    
    if (!hasActiveFilters && this.sortActive()) {
      items.sort((a, b) => {
        const aStructured = a.isStructured ? 1 : 0;
        const bStructured = b.isStructured ? 1 : 0;
        
        if (this.sortDirection() === 'asc') {
          return aStructured - bStructured; 
        } else {
          return bStructured - aStructured; 
        }
      });
    }
    
    return items;
  });

  get searchSubject() {
    return this.searchText$;
  }

  private setupSearch(): void {
    this.searchSubscription = this.searchText$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap((phrase) => {
        if (phrase.length < 2) {
          this.setItems(this.initialItems());
        }
      }),
      filter((phrase) => {
        return !!this.activeTableId() && phrase.length >= 2;
      }),
      switchMap((search) => {
        return this.tablesService.searchFiles(this.activeTableId(), search);
      })
    ).subscribe((items) => {
      this.setItems(items);
    });
  }

  toggleMaximize() {
    const currentState = this.state().maximizeState;
    console.log('Current maximize state:', currentState);
    let newState: 'minimized' | 'thirty' | 'full';
    let isMinimized = false;
    let isFullScreen = false;

    switch (currentState) {
      case 'minimized':
        newState = 'thirty';
        isMinimized = false;
        isFullScreen = false;
        console.log('Transitioning to smart first-row view');
        break;
      case 'thirty':
        newState = 'full';
        isMinimized = false;
        isFullScreen = true;
        console.log('Transitioning to full screen');
        break;
      case 'full':
      default:
        newState = 'thirty';
        isMinimized = false;
        isFullScreen = false;
        console.log('Transitioning back to smart first-row view');
        break;
    }

    const wasMinimized = this.state().isMinimized;
    this.state.update(state => ({
      ...state,
      maximizeState: newState,
      isMinimized,
      isFullScreen
    }));
    
    console.log('New state:', { maximizeState: newState, isMinimized, isFullScreen });
    console.log('Thirty screen class should be:', this.isThirtyScreen);
    
    const isNowExpanded = !isMinimized;
    if (wasMinimized && isNowExpanded) {
      this.refreshThumbnails();
    }

    if (newState === 'thirty') {
      this.setSmartFirstRowHeight();
    } else {
      this.clearSmartHeight();
    }
  }

  toggleFullScreen() {
    const wasMinimized = this.state().isMinimized;
    this.state.update(state => ({ ...state, isFullScreen: !state.isFullScreen, isMinimized: state.isFullScreen }));
    
    const isNowExpanded = this.state().isFullScreen || !this.state().isMinimized;
    if (wasMinimized && isNowExpanded) {
      this.refreshThumbnails();
    }
  }

  setMinimized(isMinimized: boolean) {
    const wasMinimized = this.state().isMinimized;
    const maximizeState = isMinimized ? 'minimized' : 'thirty';
    this.state.update(state => ({ ...state, isMinimized, maximizeState }));
    
    if (wasMinimized && !isMinimized) {
      this.refreshThumbnails();
      setTimeout(() => this.setSmartFirstRowHeight(), 0);
    } else if (isMinimized) {
      this.clearSmartHeight();
    }
  }

  setFullScreen(isFullScreen: boolean) {
    const wasMinimized = this.state().isMinimized;
    const maximizeState = isFullScreen ? 'full' : 'thirty';
    this.state.update(state => ({ ...state, isFullScreen, isMinimized: !isFullScreen, maximizeState }));
    
    if (wasMinimized && (isFullScreen || !this.state().isMinimized)) {
      this.refreshThumbnails();
    }

    if (maximizeState === 'thirty') {
      setTimeout(() => this.setSmartFirstRowHeight(), 0);
    } else if (maximizeState === 'full') {
      this.clearSmartHeight();
    }
  }

  private setSmartFirstRowHeight() {
    if (this.state().maximizeState !== 'thirty' || this.state().isMinimized) {
      return;
    }

    const secondaryToolbarHeight = 50; 
    const gridPadding = 32; 
    const thumbnailImageHeight = 121;
    const filenameAreaHeight = 20; 
    const fixedBlackBarHeight = 40; 
    const borderAndSpacingBuffer = 10;
    
    const totalHeight = secondaryToolbarHeight + gridPadding + thumbnailImageHeight + filenameAreaHeight + fixedBlackBarHeight + borderAndSpacingBuffer;
    
    const hostElement = document.querySelector('app-file-picker') as HTMLElement;
    if (hostElement) {
      hostElement.style.setProperty('height', `${totalHeight}px`, 'important');
      console.log(`Applied smart first-row height: ${totalHeight}px (image: ${thumbnailImageHeight}px + filename: ${filenameAreaHeight}px)`);
      
    }
  }

  private clearSmartHeight() {
    const hostElement = document.querySelector('app-file-picker') as HTMLElement;
    if (hostElement) {
      hostElement.style.removeProperty('height');
      console.log('Cleared smart height, reverting to CSS');
      
      // Content height is managed by flexbox, no need to clear
    }
  }

  setItems(items: FileUpload[]) {
    this.state.update(state => ({ ...state, items }));
  }

  setSearchPhrase(phrase: string) {
    this.searchText$.next(phrase);
  }

  setActiveTableId(tableId: string) {
    this.state.update(state => ({ ...state, activeTableId: tableId }));
    this.searchText$.next('');
  }

  setInitialItems(items: FileUpload[]) {
    this.state.update(state => ({ ...state, initialItems: items, items }));
  }

  toggleMinimized() {
    const wasMinimized = this.state().isMinimized;
    this.state.update(state => ({ ...state, isMinimized: !state.isMinimized }));
    
    if (wasMinimized && !this.state().isMinimized) {
      this.refreshThumbnails();
    }
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setSearchPhrase(input.value);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['files']) {
      this.initialize();
    }
    if (changes['tableId']) {
      this.setActiveTableId(this.tableId);
      if (changes['tableId'].previousValue && changes['tableId'].currentValue !== changes['tableId'].previousValue) {
        this.state.update(state => ({
          ...state,
          maximizeState: 'thirty',
          isMinimized: false,
          isFullScreen: false
        }));
        setTimeout(() => this.setSmartFirstRowHeight(), 100);
      }
    }
  }

  private async initialize() {
    this.setInitialItems(this.files);
    this.setActiveTableId(this.tableId);
    this.initializing = true;
    const isMinimized = this.isMinimizedComputed();

    if (isMinimized) {
      this.setMinimized(false);
    }

    this.updateToggleBarVisibilityAndPosition();

    this.setMinimized(isMinimized);
    this.initializing = false;

    if (this.state().maximizeState === 'thirty' && !this.state().isMinimized) {
      setTimeout(() => this.setSmartFirstRowHeight(), 100);
    }
  }

  updateToggleBarVisibilityAndPosition() {
    const filePickerContainer = document.querySelector('.file-picker-content') as HTMLElement;
    const toggleBar = document.querySelector('.toggle-bar') as HTMLElement;

    if (!filePickerContainer || !toggleBar) {
      return;
    }

    const contentHeight = filePickerContainer.scrollHeight;
    const hasOverflow = contentHeight > (180);

    this.overflows = hasOverflow;

    if (hasOverflow) {
      toggleBar.classList.add('visible');
      this.setMinimized(true);
    } else {
      toggleBar.classList.remove('visible');
      this.setMinimized(false);
    }
  }

  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = mode;
  }

  toggleViewMode() {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  toggleSort() {
    this.sortActive.set(!this.sortActive());
    this.sortDirection.set(this.sortActive() ? 'desc' : 'asc');
  }

  private previousStateBeforeFilter: 'minimized' | 'thirty' | 'full' = 'thirty';

  toggleFilter() {
    this.filterActive.set(!this.filterActive());
    if (this.filterActive()) {
      this.previousStateBeforeFilter = this.state().maximizeState;
      this.updateActiveFiltersCount();
      this.setFullScreen(true);
    } else {
      this.restoreStateAfterFilter();
    }
  }

  private restoreStateAfterFilter() {
    switch (this.previousStateBeforeFilter) {
      case 'minimized':
        this.setMinimized(true);
        break;
      case 'thirty':
        this.state.update(state => ({
          ...state,
          maximizeState: 'thirty',
          isMinimized: false,
          isFullScreen: false
        }));
        setTimeout(() => this.setSmartFirstRowHeight(), 0);
        break;
      case 'full':
        this.setFullScreen(true);
        break;
    }
  }

  private refreshThumbnails() {
    const currentItems = this.items();
    
    const thumbnailRefreshPromises = currentItems.map((file) => {
      if (file.image?.resourceKey) {
        return new Promise<void>((resolve) => {
          this.tablesService
            .getSignedImageUrl(file.image!.resourceKey)
            .subscribe({
              next: (res: GetSignedURLDto) => {
                if (res.data && file.image) {
                  file.image.url = res.data;
                }
                resolve();
              },
              error: () => resolve(),
            });
        });
      }
      return Promise.resolve();
    });

    Promise.all(thumbnailRefreshPromises).then(() => {
      this.setItems([...currentItems]);
    });
  }

  toggleMinimize() {
    const wasMinimized = this.state().isMinimized;
    this.state.update(state => ({
      ...state,
      isMinimized: true,
      isFullScreen: false,
      maximizeState: 'minimized'
    }));
    
    this.clearSmartHeight();
  }

  get uploadingSlots(): number[] {
    return Array(this.filesCurrentlyUploading).fill(0);
  }

  selectFile(index: number) {
    const selectedFile = this.itemsToShow()[index];
    if (selectedFile) {
      const actualIndex = this.files.findIndex(file => file.id === selectedFile.id);
      if (actualIndex !== -1) {
        this.fileSelected.emit(actualIndex);
      }
    }
  }

  triggerFileUpload() {
    if (this.isGeneratingSchema) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  handleInputChange(event: any) {
    if (this.isGeneratingSchema) {
      return;
    }
    
    const files = event.target?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
    
    if (event.target) {
      event.target.value = '';
    }
  }

  private isValid(file: DataTransferItem|File): boolean {
    const typeValid = this.valid_MIME_types.includes(file.type);
    if (typeValid) {
      return true;
    }

    let fileName = '';
    if (file instanceof File) {
      fileName = file.name || "";
    } else {
      fileName = file.getAsFile()?.name || "";
    }

    if (fileName) {
      const extension = '.' + (fileName.toLowerCase().split(".").pop() || '');
      return this.valid_extensions.includes(extension);
    }

    return false;
  }

  private resetDragState() {
    this.cDragOver = false;
    this.cDragIdle = true;
    this.cDragInvalid = false;
  }

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    if (this.isGeneratingSchema) {
      return false;
    }
    
    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
      this.dragLeaveTimeout = null;
    }
    
    this.isDragOver = true;
    this.cDragOver = true;
    this.cDragIdle = false;
    return false;
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    if (this.isGeneratingSchema) {
      return false;
    }
    
    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
      this.dragLeaveTimeout = null;
    }
    
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    
    this.isDragOver = true;
    this.cDragOver = true;
    this.cDragIdle = false;
    return false;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    if (this.isGeneratingSchema) {
      return;
    }
    
    const target = event.target as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;
    
    if (!relatedTarget || !target.closest('app-file-picker') || !relatedTarget.closest('app-file-picker')) {
      event.preventDefault();
      event.stopPropagation();
      
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
      }
      
     
      this.dragLeaveTimeout = setTimeout(() => {
        this.isDragOver = false;
        this.resetDragState();
        this.dragLeaveTimeout = null;
      }, 50);
    }
  }

  @HostListener('drop', ['$event'])
  onFileDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
      this.dragLeaveTimeout = null;
    }
    
    if (this.isGeneratingSchema) {
      this.isDragOver = false;
      this.resetDragState();
      return false;
    }

    this.isDragOver = false;
    this.resetDragState();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
    
    return false;
  }


  handleFiles(files: FileList) {
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];
    const oversizedFiles: File[] = [];

    for (const file of fileArray) {
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        if (!oversizedFiles.some(f => f.name === file.name)) {
          oversizedFiles.push(file);
        }
        continue;
      }

      if (this.isValid(file)) {
        if (!validFiles.some(f => f.name === file.name)) {
          validFiles.push(file);
        }
      } else {
        if (!invalidFiles.some(f => f.name === file.name)) {
          invalidFiles.push(file);
        }
      }
    }

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      const fileSizes = oversizedFiles.map(f => (f.size / (1024 * 1024)).toFixed(1)).join(', ');
      this.toastService.showError(
        'File Size Error', 
        `The following files exceed the 50MB limit and were not uploaded: ${fileNames} (${fileSizes}MB each)`
      );
    }

    if (validFiles.length > 0) {
      this.fileDropped.emit({ event: validFiles, isDrop: true });
      setTimeout(() => this.updateToggleBarVisibilityAndPosition(), 0);
    }

    if (invalidFiles.length > 0) {
      this.fileInvalid.emit(invalidFiles);
    }

    return false;
  }

  setUploadDateFilter(filter: string) {
    if (this.uploadDateFilter !== filter) {
      this.structuringDateFilter = '';
      this.alphanumericFilter = '';
      this.fileSizeFilter = '';
      this.uploadDateFilter = filter;
    } else {
      this.uploadDateFilter = '';
    }
    this.updateActiveFiltersCount();
  }

  setStructuringDateFilter(filter: string) {
    if (this.structuringDateFilter !== filter) {
      this.uploadDateFilter = '';
      this.alphanumericFilter = '';
      this.fileSizeFilter = '';
      this.structuringDateFilter = filter;
    } else {
      this.structuringDateFilter = '';
    }
    this.updateActiveFiltersCount();
  }

  setAlphanumericFilter(filter: string) {
    if (this.alphanumericFilter !== filter) {
      this.uploadDateFilter = '';
      this.structuringDateFilter = '';
      this.fileSizeFilter = '';
      this.alphanumericFilter = filter;
    } else {
      this.alphanumericFilter = '';
    }
    this.updateActiveFiltersCount();
  }

  setFileTypeFilter(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.fileTypeFilter = select.value;
    this.updateActiveFiltersCount();
  }

  setFileSizeFilter(filter: string) {
    if (this.fileSizeFilter !== filter) {
      this.uploadDateFilter = '';
      this.structuringDateFilter = '';
      this.alphanumericFilter = '';
      this.fileSizeFilter = filter;
    } else {
      this.fileSizeFilter = '';
    }
    this.updateActiveFiltersCount();
  }

  resetUploadDateFilter() {
    this.uploadDateFilter = '';
    this.updateActiveFiltersCount();
  }

  resetStructuringDateFilter() {
    this.structuringDateFilter = '';
    this.updateActiveFiltersCount();
  }

  resetAlphanumericFilter() {
    this.alphanumericFilter = '';
    this.updateActiveFiltersCount();
  }

  resetFileTypeFilter() {
    this.fileTypeFilter = '';
    const dropdown = document.querySelector('.filter-dropdown') as HTMLSelectElement;
    if (dropdown) {
      dropdown.selectedIndex = 0;
    }
    this.updateActiveFiltersCount();
  }

  resetFileSizeFilter() {
    this.fileSizeFilter = '';
    this.updateActiveFiltersCount();
  }

  resetAllFilters() {
    this.uploadDateFilter = '';
    this.structuringDateFilter = '';
    this.alphanumericFilter = '';
    this.fileTypeFilter = '';
    this.fileSizeFilter = '';
    
    const dropdown = document.querySelector('.filter-dropdown') as HTMLSelectElement;
    if (dropdown) {
      dropdown.selectedIndex = 0;
    }
    
    this.updateActiveFiltersCount();
    this.applyFilters();
  }

  applyFiltersAndClose() {
    this.applyFilters();
    this.filterActive.set(false);
    this.restoreStateAfterFilter();
  }

  private updateActiveFiltersCount() {
    let count = 0;
    if (this.uploadDateFilter) count++;
    if (this.structuringDateFilter) count++;
    if (this.alphanumericFilter) count++;
    if (this.fileTypeFilter) count++;
    if (this.fileSizeFilter) count++;
    
    this.activeFiltersCount.set(count);
  }

  private applyFilters() {
    console.log('Applying filters:', {
      uploadDate: this.uploadDateFilter,
      structuringDate: this.structuringDateFilter,
      alphanumeric: this.alphanumericFilter,
      fileType: this.fileTypeFilter,
      fileSize: this.fileSizeFilter
    });

    let filteredItems = [...this.initialItems()];
    console.log('Initial items count:', filteredItems.length);

    if (this.fileTypeFilter) {
      const beforeCount = filteredItems.length;
      filteredItems = filteredItems.filter(file => {
        const extension = file.fileName.split('.').pop()?.toLowerCase() || '';
        return extension === this.fileTypeFilter.toLowerCase();
      });
      console.log(`File type filter applied: ${beforeCount} -> ${filteredItems.length} files`);
    }

    const hasFilters = this.uploadDateFilter || this.structuringDateFilter || this.alphanumericFilter || this.fileSizeFilter;
    if (hasFilters) {
      filteredItems.sort((a, b) => {
        if (this.uploadDateFilter) {
          const aDate = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
          const bDate = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
          
          if (this.uploadDateFilter === 'newest') {
            return bDate - aDate; 
          } else if (this.uploadDateFilter === 'oldest') {
            return aDate - bDate; 
          }
        }

        if (this.structuringDateFilter && !this.uploadDateFilter) {
          const aStructuredDate = (a as any).structuredAt ? new Date((a as any).structuredAt).getTime() : 0;
          const bStructuredDate = (b as any).structuredAt ? new Date((b as any).structuredAt).getTime() : 0;
          
          if (this.structuringDateFilter === 'newest') {
            return bStructuredDate - aStructuredDate; 
          } else if (this.structuringDateFilter === 'oldest') {
            return aStructuredDate - bStructuredDate; 
          }
        }

        if (this.fileSizeFilter && !this.uploadDateFilter && !this.structuringDateFilter) {
          const aSize = a.sizeKb || 0;
          const bSize = b.sizeKb || 0;
          
          if (this.fileSizeFilter === 'small') {
            return aSize - bSize; 
          } else if (this.fileSizeFilter === 'big') {
            return bSize - aSize; 
          }
        }

        if (this.alphanumericFilter && !this.uploadDateFilter && !this.structuringDateFilter && !this.fileSizeFilter) {
          const aName = a.fileName.toLowerCase();
          const bName = b.fileName.toLowerCase();
          
          if (this.alphanumericFilter === 'az') {
            return aName.localeCompare(bName); // A to Z
          } else if (this.alphanumericFilter === 'za') {
            return bName.localeCompare(aName); // Z to A
          }
        }

        return 0;
      });
      console.log('Sorting applied based on active filters');
    }

    console.log('Final filtered items count:', filteredItems.length);
    this.setItems(filteredItems);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const filterWindow = target.closest('.filter-window');
    const filterButton = target.closest('.filter-button');
    const fileMenu = target.closest('.file-menu');
    const fileMenuButton = target.closest('.file-menu-button');
    
    if (!filterWindow && !filterButton && this.filterActive()) {
      this.filterActive.set(false);
      this.restoreStateAfterFilter();
    }
    
    if (!fileMenu && !fileMenuButton && this.activeFileMenu) {
      this.activeFileMenu = null;
    }
  }

  toggleFileMenu(event: Event, fileId: string) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.activeFileMenu = this.activeFileMenu === fileId ? null : fileId;
  }

  onRenameFile(event: Event, file: any) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.activeFileMenu = null; 
    setTimeout(() => {
      this.isRenaming = file.id;
      this.newFileName = file.fileName.split('.').slice(0, -1).join('.');
    }, 0);
  }

  confirmRename(file: any) {
    if (!this.newFileName.trim()) {
      this.toastService.showError('Error', 'File name cannot be empty');
      return;
    }

    const extension = file.fileName.split('.').pop();
    const fullFileName = extension ? `${this.newFileName.trim()}.${extension}` : this.newFileName.trim();

    this.tablesService.renameFile(this.activeTableId(), file.id, fullFileName).subscribe({
      next: (response: any) => {
        if (response.data) {
          const currentItems = this.items();
          const updatedItems = currentItems.map(item => 
            item.id === file.id ? { ...item, fileName: fullFileName } : item
          );
          this.setItems(updatedItems);
          
          const initialItems = this.initialItems();
          const updatedInitialItems = initialItems.map(item => 
            item.id === file.id ? { ...item, fileName: fullFileName } : item
          );
          this.state.update(state => ({ ...state, initialItems: updatedInitialItems }));
          
          this.toastService.showInfo('Success', 'File renamed successfully');
        }
        this.isRenaming = null;
        this.newFileName = '';
      },
      error: (error) => {
        this.toastService.showError('Error', error.error?.message || 'Failed to rename file');
        this.isRenaming = null;
        this.newFileName = '';
      }
    });
  }

  cancelRename() {
    this.isRenaming = null;
    this.newFileName = '';
  }

  onDownloadFile(file: any) {
    this.tablesService.downloadFile(this.activeTableId(), file.id).subscribe({
      next: (response: any) => {
        const blob = response as Blob;
        this.tablesService.triggerDownload(blob, file.fileName);
        this.toastService.showInfo('Success', 'File downloaded successfully');
      },
      error: (error) => {
        this.toastService.showError('Error', error.error?.message || 'Failed to download file');
      }
    });
    this.activeFileMenu = null;
  }

  onDeleteFile(file: any) {
    this.fileToDelete = file;
    this.activeFileMenu = null;
    this.modalService.open(this.deleteFileModal, { centered: true });
  }

  confirmDeleteFile() {
    if (this.fileToDelete) {
      this.tablesService.deleteFile(this.activeTableId(), this.fileToDelete.id).subscribe({
        next: (response: any) => {
          const currentItems = this.items();
          const updatedItems = currentItems.filter(item => item.id !== this.fileToDelete.id);
          this.setItems(updatedItems);
          
          const initialItems = this.initialItems();
          const updatedInitialItems = initialItems.filter(item => item.id !== this.fileToDelete.id);
          this.state.update(state => ({ ...state, initialItems: updatedInitialItems }));
          
          this.toastService.showInfo('Success', 'File deleted successfully');
          this.fileToDelete = null;
        },
        error: (error) => {
          this.toastService.showError('Error', error.error?.message || 'Failed to delete file');
          this.fileToDelete = null;
        }
      });
    }
  }

  getFileType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return extension.toUpperCase();
  }

  uploadMoreFilesAndExpand() {
    if (this.isGeneratingSchema) {
      return;
    }
    
    this.state.update(state => ({
      ...state,
      maximizeState: 'thirty',
      isMinimized: false,
      isFullScreen: false
    }));
    
    setTimeout(() => this.setSmartFirstRowHeight(), 0);
    
    this.blackBarFileInput.nativeElement.click();
  }
}
